import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plane, Clock, CheckCircle2, AlertCircle } from "lucide-react";

/** Inline flight lookup widget for booking form. */
export default function FlightLookup({ value, onChange, onResult, pickupDatetime }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const lookup = async () => {
    if (!value || value.trim().length < 3) {
      setErr("Enter a valid flight number (e.g. AA1234)");
      return;
    }
    setBusy(true); setErr(null);
    try {
      // Anchor the lookup to the pickup time so a repeating flight number
      // (e.g. daily AA1234) resolves to the customer's flight, not a past one.
      const near = pickupDatetime ? `?near=${encodeURIComponent(pickupDatetime)}` : "";
      const r = await api.get(`/flights/${encodeURIComponent(value.trim().toUpperCase())}${near}`);
      setData(r.data);
      onResult && onResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Flight not found");
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="field-label">Flight number (optional)</label>
        <div className="flex gap-2">
          <input
            data-testid="flight-number-input"
            className="field-input"
            value={value || ""}
            onChange={e => { onChange(e.target.value.toUpperCase()); setData(null); }}
            placeholder="UA123 · AAL2412"
          />
          <button
            data-testid="flight-lookup-btn"
            type="button"
            onClick={lookup}
            disabled={busy || !value}
            className="btn-ghost !py-2 !px-4 text-[11px] shrink-0"
          >
            {busy ? "…" : "Track"}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-red-400 flex items-center gap-2"><AlertCircle size={14}/>{err}</div>
      )}

      {data && (
        <div data-testid="flight-result" className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 text-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Plane size={14}/>
              <span className="font-serif text-base">{data.ident}</span>
              {data.operator && <span className="text-xs text-[#A1A1AA]">· {data.operator}</span>}
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">{data.status || "Scheduled"}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[#71717A] uppercase tracking-[0.18em]">From</div>
              <div className="text-[#F5F2EB]">{data.origin || "—"}</div>
            </div>
            <div>
              <div className="text-[#71717A] uppercase tracking-[0.18em]">To</div>
              <div className="text-[#F5F2EB]">{data.destination || "—"}</div>
            </div>
            {data.scheduled_in && (
              <div className="col-span-2">
                <div className="text-[#71717A] uppercase tracking-[0.18em]">Scheduled arrival</div>
                <div className="text-[#F5F2EB] flex items-center gap-1"><Clock size={12}/>{new Date(data.scheduled_in).toLocaleString()}</div>
              </div>
            )}
            {data.estimated_in && data.estimated_in !== data.scheduled_in && (
              <div className="col-span-2">
                <div className="text-[#D4AF37] uppercase tracking-[0.18em]">Estimated arrival</div>
                <div className="text-[#F5F2EB]">{new Date(data.estimated_in).toLocaleString()}</div>
              </div>
            )}
          </div>
          {data.source === "MOCKED" && (
            <div className="mt-2 text-[9px] uppercase tracking-[0.2em] text-[#71717A]">Source: MOCKED — add FLIGHTAWARE_API_KEY for live data</div>
          )}
          <div className="mt-2 text-[10px] text-[#10B981] flex items-center gap-1"><CheckCircle2 size={12}/>We'll auto-adjust your pickup time if your flight is delayed.</div>
        </div>
      )}
    </div>
  );
}
